import { Link } from "@tanstack/react-router";
import type { BlogListItem } from "../lib/blog.functions";
import { type BlogLocale, SUPPORTED_LOCALES, getLocale } from "../lib/blog-locales";
import { CallLabel } from "./call-label";
import { heroSrc } from "../lib/blog-image";
import { openQuoteModal } from "./quote-modal";

function formatDate(value: string, locale: BlogLocale): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleDateString(locale.dateLocale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  }
}

type Props = {
  articles: BlogListItem[];
  heading?: string;
  /** @deprecated use locale instead */
  isEs?: boolean;
  /** Locale code — drives all UI strings and date formatting */
  localeCode?: string;
  className?: string;
  /**
   * Desktop sticky mode: the article list scrolls inside the column so the
   * quote/call card at the bottom always stays on screen.
   */
  fitViewport?: boolean;
};

export function BlogSidebar({
  articles,
  heading,
  isEs = false,
  localeCode,
  className = "",
  fitViewport = false,
}: Props) {
  if (articles.length === 0) return null;
  const locale = localeCode ? getLocale(localeCode) : isEs ? getLocale("es") : getLocale("en");
  const ui = locale.ui;

  return (
    <aside
      className={`${fitViewport ? "flex min-h-0 flex-col gap-8" : "space-y-8"} ${className}`}
      aria-label={heading ?? ui.moreArticles}
      dir={locale.dir}
    >
      <section
        className={`rounded-xl border border-gray-200 bg-white p-5 shadow-sm ${
          fitViewport ? "min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-width:thin]" : ""
        }`}
      >
        <h2 className="font-[Oswald,sans-serif] text-lg font-bold uppercase tracking-wide text-[var(--brand-deep-blue)]">
          {heading ?? ui.moreArticles}
        </h2>
        <ul className="mt-4 space-y-4">
          {articles.map((a) => (
            <li key={a.slug} className="group flex gap-3">
              <Link
                to="/blog/$slug"
                params={{ slug: a.slug }}
                className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-[var(--brand-light-gray)]"
                tabIndex={a.hero_image_url ? undefined : -1}
              >
                {a.hero_image_url ? (
                  <img
                    src={heroSrc(a.hero_image_url, 400)}
                    alt={a.title}
                    width={160}
                    height={128}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : null}
              </Link>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold leading-snug text-[var(--brand-charcoal)] group-hover:text-[var(--brand-deep-blue)]">
                  <Link to="/blog/$slug" params={{ slug: a.slug }} className="line-clamp-3">
                    {a.title}
                  </Link>
                </h3>
                <time
                  suppressHydrationWarning
                  dateTime={a.created_at}
                  className="mt-1 block text-[11px] font-semibold uppercase tracking-wide text-[var(--brand-charcoal)]/55"
                >
                  {formatDate(a.created_at, locale)}
                </time>
                {a.read_minutes > 0 ? (
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--brand-charcoal)]/55">
                    {ui.minRead(a.read_minutes)}
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section
        className={`rounded-xl bg-[var(--brand-deep-blue)] p-6 text-center text-white shadow-sm ${
          fitViewport ? "shrink-0" : ""
        }`}
      >
        <h2 className="font-[Oswald,sans-serif] text-xl font-bold uppercase leading-tight">
          {ui.needQuote}
        </h2>
        <p className="mt-2 text-sm text-white/80">{ui.freeEstimates}</p>
        <button
          type="button"
          onClick={() => openQuoteModal()}
          className="mt-4 block w-full rounded-md bg-[var(--brand-red)] px-5 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90"
        >
          {ui.getFreeQuote}
        </button>
        <a
          href="tel:+15189001913"
          className="mt-3 block rounded-md bg-white px-5 py-3 text-sm font-bold text-[var(--brand-deep-blue)] transition-opacity hover:opacity-90"
        >
          <CallLabel label={ui.callLabel} />
        </a>
      </section>
    </aside>
  );
}
